import { Injectable, inject, signal, computed } from '@angular/core';
import { catchError, finalize, of, tap } from 'rxjs';
import { AuthApiService } from './auth.service';
import { BrandingService } from './branding.service';

/**
 * Logged-in user profile from `GET /user/profile`, exposed as signals for layout + profile page.
 */
@Injectable({ providedIn: 'root' })
export class AdminUserProfileState {
  private readonly authApi = inject(AuthApiService);
  /** Feed company_logo into branding when the profile loads. */
  private readonly branding = inject(BrandingService);

  readonly data = signal<Record<string, unknown> | null>(null);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  /**
   * Role names the profile response carries, in the order it lists them.
   *
   * `myProfile` selects `user_roles[].role.role_name` and filters to active roles, so anything
   * here is a role the user currently holds. A user may hold more than one.
   */
  private rolesOf(user: Record<string, unknown>): string[] {
    const assignments = user['user_roles'];
    if (!Array.isArray(assignments)) return [];
    return assignments
      .map((entry) => (entry as { role?: { role_name?: unknown } })?.role?.role_name)
      .filter((name): name is string => typeof name === 'string' && name.trim() !== '');
  }

  /** Shaped reads — adjust if your `data` payload differs. */
  readonly userDisplay = computed(() => {
    const d = this.data();
    if (!d)
      return {
        name: '',
        email: '',
        initials: '?',
        avatarUrl: null as string | null,
        roles: [] as string[],
        role: '',
      };
    const user = (d['userData'] ?? d['user'] ?? d) as Record<string, unknown>;
    const email = (user['email_id'] ?? user['email'] ?? '') as string;
    const first = (user['first_name'] as string) || '';
    const last = (user['last_name'] as string) || '';
    const full = (user['full_name'] as string) || [first, last].filter(Boolean).join(' ').trim();
    const name = full || email || 'User';
    const initials = (user['initials'] as string) || name.replaceAll(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase() || '?';
    const rawPic = user['profile_picture'] ?? user['profilePicture'];
    const avatarUrl =
      typeof rawPic === 'string' && rawPic.length > 0
        ? rawPic
        : null;
    const roles = this.rolesOf(user);
    return { name, email, initials, avatarUrl, roles, role: roles.join(', ') };
  });

  clear(): void {
    this.data.set(null);
    this.error.set(null);
  }

  loadFromApi(): void {
    this.loading.set(true);
    this.error.set(null);
    this.authApi
      .getUserProfile()
      .pipe(
        tap((res) => {
          if (res.status === true) {
            this.data.set(res.data as Record<string, unknown>);
            // Update tenant logo from profile response.
            this.branding.setFromProfile((res.data as any)?.company_logo ?? null);
          } else {
            this.error.set(res.message || 'Profile request failed');
          }
        }),
        catchError((e: Error) => {
          this.error.set(e?.message || 'Profile request failed');
          return of(null);
        }),
        finalize(() => this.loading.set(false))
      )
      .subscribe();
  }
}
